import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import classnames from 'classnames';
import {
  Nav,
  NavItem,
  NavLink,
  UncontrolledTooltip,
} from 'reactstrap';
import { useIntl } from 'react-intl';

import { switchToUser } from '../../../actions/Auth';

import { userCan, userIsSuperAdmin } from '../../../helpers/Auth';
import { getSetting } from '../../../helpers/Settings';
import { canUserManageShare } from '../../../helpers/Share';

import LanguageSwitch from '../../LanguageSwitch';
import UpgradePlanButton from '../../UpgradePlanButton';

const AdminUserEditTabs = ({
  activeTab,
  profile,
  isSelfEdit,
  handleSwitchTab,
}) => {
  const intl = useIntl();

  return (
    <div className="settings-tab-wrapper">
      <Nav tabs className="settings-tabs icon-tabs">
        <NavItem>
          <NavLink
            className={classnames({ active: activeTab === 'profile' })}
            onClick={() => { handleSwitchTab('profile'); }}
          >
            <i className="fa-solid fa-user" />
            <span>{intl.formatMessage({ defaultMessage: 'Profile' })}</span>
          </NavLink>
        </NavItem>
        {userCan('manage_settings') && (
          <NavItem>
            <NavLink
              className={classnames({ active: activeTab === 'password' })}
              onClick={() => { handleSwitchTab('password'); }}
            >
              <i className="fa-solid fa-star-of-life" />
              <span>{intl.formatMessage({ defaultMessage: 'Password' })}</span>
            </NavLink>
          </NavItem>
        )}
        {userCan('manage_settings') && (
          <NavItem>
            <NavLink
              className={classnames({ active: activeTab === '2fa' })}
              onClick={() => { handleSwitchTab('2fa'); }}
            >
              <i className="fa-solid fa-shield" />
              <span>{intl.formatMessage({ defaultMessage: '2FA' })}</span>
            </NavLink>
          </NavItem>
        )}
        <NavItem>
          <NavLink
            className={classnames({ active: activeTab === 'permissions' })}
            onClick={() => { handleSwitchTab('permissions'); }}
          >
            <i className="fa-solid fa-users" />
            <span>{intl.formatMessage({ defaultMessage: 'Role & Teams' })}</span>
          </NavLink>
        </NavItem>
        {profile.uuid && (
          <>
            <NavItem>
              <NavLink
                className={classnames({ active: activeTab === 'notifications' })}
                onClick={() => { handleSwitchTab('notifications'); }}
              >
                <i className="fa-solid fa-bell" />
                <span>{intl.formatMessage({ defaultMessage: 'Notifications' })}</span>
              </NavLink>
            </NavItem>
            {canUserManageShare() && (
              <NavItem>
                <NavLink
                  className={classnames({ active: activeTab === 'share-settings' })}
                  onClick={() => handleSwitchTab('share-settings')}
                >
                  <i className="fa-solid fa-share-nodes" />
                  <span>{intl.formatMessage({ defaultMessage: 'Share Settings' })}</span>
                  <UpgradePlanButton
                    feature="share-templates"
                    responsive={true}
                  />
                </NavLink>
              </NavItem>
            )}
            {!getSetting('file_name_prefix_and_suffix_force_workspace') && (
              <NavItem>
                <NavLink
                  className={classnames({ active: activeTab === 'prefix-and-suffix' })}
                  onClick={() => { handleSwitchTab('prefix-and-suffix'); }}
                >
                  <i className="fa-solid fa-input-text" />
                  <span>{intl.formatMessage({ defaultMessage: 'File name prefix and suffix' })}</span>
                  <UpgradePlanButton
                    feature="file-name-prefix-suffix"
                    responsive={true}
                  />
                </NavLink>
              </NavItem>
            )}
            {(getSetting('public_api_enabled') && !userIsSuperAdmin()) && (
              <NavItem>
                <NavLink
                  className={classnames({ active: activeTab === 'api-access' })}
                  onClick={() => { handleSwitchTab('api-access'); }}
                >
                  <i className="fa-solid fa-input-text" />
                  <span>{intl.formatMessage({ defaultMessage: 'API access' })}</span>
                </NavLink>
              </NavItem>
            )}
          </>
        )}
      </Nav>
      <div className="profile-edit-actions">
        <Nav>
          {isSelfEdit && (
            <LanguageSwitch />
          )}
          {(!isSelfEdit && userCan('manage_settings')) && (
            <NavItem>
              <NavLink
                id="switch-to-link"
                onClick={() => { switchToUser(profile.uuid); }}
              >
                <i className="far fa-repeat-alt" />
              </NavLink>
              <UncontrolledTooltip fade={false} target="switch-to-link">
                {intl.formatMessage({ defaultMessage: 'Switch to user' })}
              </UncontrolledTooltip>
            </NavItem>
          )}
          <NavItem>
            <NavLink
              tag={Link}
              id="view-profile-link"
              to={`/profile/${profile.uuid}`}
            >
              <i className="far fa-user" />
            </NavLink>
            <UncontrolledTooltip fade={false} target="view-profile-link">
              {intl.formatMessage({ defaultMessage: 'View profile' })}
            </UncontrolledTooltip>
          </NavItem>
        </Nav>
      </div>
    </div>
  );
};

AdminUserEditTabs.propTypes = {
  activeTab: PropTypes.string.isRequired,
  profile: PropTypes.object.isRequired,
  isSelfEdit: PropTypes.bool.isRequired,
  handleSwitchTab: PropTypes.func.isRequired,
};

export default AdminUserEditTabs;
